(() => {
  'use strict';

  const selectionStorageKey = 'avt-fitment-selection';
  const garageStorageKey = 'avt-garage-v1';
  const maximumBikes = 12;

  const exactKey = (selection) => {
    const make = String(selection?.make ?? '').trim();
    const model = String(selection?.model ?? '').trim();
    const year = Number(selection?.year);
    const hasControlCharacters = /[\u0000-\u001F\u007F]/;
    if (
      !make
      || !model
      || make.length > 80
      || model.length > 80
      || make.includes('::')
      || model.includes('::')
      || hasControlCharacters.test(make)
      || hasControlCharacters.test(model)
      || !Number.isInteger(year)
      || year < 1900
      || year > 2200
    ) {
      return '';
    }
    return `${make}::${model}::${year}`;
  };

  const normalizeBike = (selection) => {
    const bikeKey = exactKey(selection);
    const type = String(selection?.type ?? '').trim();
    if (
      !bikeKey
      || type.length > 80
      || type.includes('::')
      || /[\u0000-\u001F\u007F]/.test(type)
      || !Array.isArray(selection?.compatibilityKeys)
      || selection.compatibilityKeys.length > 128
    ) {
      return null;
    }
    const compatibilityKeys = [...new Set(
      selection.compatibilityKeys
        .filter(
          (key) =>
            typeof key === 'string'
            && key.length >= 1
            && key.length <= 180
            && !/[\u0000-\u001F\u007F]/.test(key)
        )
    )];
    if (!compatibilityKeys.includes(bikeKey)) return null;
    return {
      make: String(selection.make).trim(),
      type,
      model: String(selection.model).trim(),
      year: String(Number(selection.year)),
      compatibilityKeys,
    };
  };

  const bikeIsValid = (selection) => Boolean(normalizeBike(selection));

  const emptyGarage = () => ({
    schemaVersion: 1,
    kind: 'avt_garage',
    activeBikeKey: '',
    bikes: [],
  });

  const clone = (value) => JSON.parse(JSON.stringify(value));

  const normalizeGarage = (value) => {
    let parsed = value;
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        return null;
      }
    }
    if (
      !parsed
      || typeof parsed !== 'object'
      || Array.isArray(parsed)
      || parsed.schemaVersion !== 1
      || parsed.kind !== 'avt_garage'
      || !Array.isArray(parsed.bikes)
    ) {
      return null;
    }
    const bikes = [];
    const seen = new Set();
    for (const candidate of parsed.bikes) {
      const bike = normalizeBike(candidate);
      const bikeKey = exactKey(bike);
      if (!bike || seen.has(bikeKey)) continue;
      seen.add(bikeKey);
      bikes.push(bike);
    }
    if (bikes.length > maximumBikes) return null;
    const requestedActive = String(parsed.activeBikeKey ?? '');
    const activeBikeKey = requestedActive === ''
      ? ''
      : seen.has(requestedActive)
        ? requestedActive
        : exactKey(bikes[0]);
    return {
      schemaVersion: 1,
      kind: 'avt_garage',
      activeBikeKey,
      bikes,
    };
  };

  const readStorage = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const readLegacyBike = () => {
    try {
      return normalizeBike(JSON.parse(readStorage(selectionStorageKey)));
    } catch {
      return null;
    }
  };

  const readInternal = () => {
    const stored = normalizeGarage(readStorage(garageStorageKey));
    const legacy = readLegacyBike();
    if (stored && (stored.bikes.length > 0 || !legacy)) return stored;
    if (!legacy) return emptyGarage();
    return {
      schemaVersion: 1,
      kind: 'avt_garage',
      activeBikeKey: exactKey(legacy),
      bikes: [legacy],
    };
  };

  const activeBikeFrom = (garage) =>
    garage.bikes.find((bike) => exactKey(bike) === garage.activeBikeKey) || null;

  const writeSelection = (selection) => {
    try {
      if (selection) {
        window.localStorage.setItem(
          selectionStorageKey,
          JSON.stringify(selection),
        );
      } else {
        window.localStorage.removeItem(selectionStorageKey);
      }
      return true;
    } catch {
      return false;
    }
  };

  const writeGarage = (garage) => {
    try {
      window.localStorage.setItem(garageStorageKey, JSON.stringify(garage));
      return true;
    } catch {
      return false;
    }
  };

  const dispatch = (garage, selection, reason, activeChanged) => {
    const detail = {
      garage: clone(garage),
      reason,
      selection: selection ? clone(selection) : {},
    };
    document.dispatchEvent(new CustomEvent('avt:garage-change', { detail }));
    if (activeChanged) {
      document.dispatchEvent(
        new CustomEvent('avt:fitment-change', {
          detail: { selection: detail.selection },
        }),
      );
    }
  };

  const persist = (garage, previous, reason, forceFitment = false) => {
    const normalized = normalizeGarage(garage);
    if (!normalized) return false;
    const selection = activeBikeFrom(normalized);
    if (!writeSelection(selection)) return false;
    if (!writeGarage(normalized)) {
      writeSelection(activeBikeFrom(previous));
      return false;
    }
    const activeChanged = (
      forceFitment
      || previous.activeBikeKey !== normalized.activeBikeKey
    );
    dispatch(normalized, selection, reason, activeChanged);
    return true;
  };

  const read = () => clone(readInternal());

  const activeBike = () => {
    const bike = activeBikeFrom(readInternal());
    return bike ? clone(bike) : {};
  };

  const saveBike = (selection) => {
    const bike = normalizeBike(selection);
    const previous = readInternal();
    if (!bike) {
      return {
        ok: false,
        reason: 'invalid_bike',
        garage: clone(previous),
      };
    }
    const bikeKey = exactKey(bike);
    const exists = previous.bikes.some(
      (candidate) => exactKey(candidate) === bikeKey
    );
    if (!exists && previous.bikes.length >= maximumBikes) {
      return {
        ok: false,
        reason: 'garage_full',
        garage: clone(previous),
      };
    }
    const garage = {
      schemaVersion: 1,
      kind: 'avt_garage',
      activeBikeKey: bikeKey,
      bikes: [
        bike,
        ...previous.bikes.filter(
          (candidate) => exactKey(candidate) !== bikeKey
        ),
      ],
    };
    if (!persist(garage, previous, exists ? 'bike_updated' : 'bike_added', true)) {
      return {
        ok: false,
        reason: 'storage_unavailable',
        garage: clone(previous),
      };
    }
    return {
      ok: true,
      reason: exists ? 'bike_updated' : 'bike_added',
      garage: clone(garage),
    };
  };

  const activateBike = (bikeKey) => {
    const previous = readInternal();
    const bike = previous.bikes.find(
      (candidate) => exactKey(candidate) === bikeKey
    );
    if (!bike) {
      return {
        ok: false,
        reason: 'bike_not_found',
        garage: clone(previous),
      };
    }
    if (previous.activeBikeKey === bikeKey) {
      return {
        ok: true,
        reason: 'already_active',
        garage: clone(previous),
      };
    }
    const garage = {
      ...previous,
      activeBikeKey: bikeKey,
    };
    if (!persist(garage, previous, 'bike_activated')) {
      return {
        ok: false,
        reason: 'storage_unavailable',
        garage: clone(previous),
      };
    }
    return {
      ok: true,
      reason: 'bike_activated',
      garage: clone(garage),
    };
  };

  const removeBike = (bikeKey) => {
    const previous = readInternal();
    if (!previous.bikes.some((candidate) => exactKey(candidate) === bikeKey)) {
      return {
        ok: false,
        reason: 'bike_not_found',
        garage: clone(previous),
      };
    }
    const bikes = previous.bikes.filter(
      (candidate) => exactKey(candidate) !== bikeKey
    );
    const activeBikeKey = previous.activeBikeKey === bikeKey ? '' : previous.activeBikeKey;
    const garage = {
      schemaVersion: 1,
      kind: 'avt_garage',
      activeBikeKey,
      bikes,
    };
    if (!persist(garage, previous, 'bike_removed')) {
      return {
        ok: false,
        reason: 'storage_unavailable',
        garage: clone(previous),
      };
    }
    return {
      ok: true,
      reason: 'bike_removed',
      garage: clone(garage),
    };
  };

  const clearActiveBike = () => {
    const previous = readInternal();
    if (!previous.activeBikeKey) {
      return { ok: true, reason: 'already_clear', garage: clone(previous) };
    }
    const garage = { ...previous, activeBikeKey: '' };
    if (!persist(garage, previous, 'bike_cleared')) {
      return { ok: false, reason: 'storage_unavailable', garage: clone(previous) };
    }
    return { ok: true, reason: 'bike_cleared', garage: clone(garage) };
  };

  const initialize = () => {
    const rawGarage = readStorage(garageStorageKey);
    const garage = normalizeGarage(rawGarage);
    const legacy = readLegacyBike();
    if (garage) {
      if (garage.bikes.length === 0 && legacy) {
        writeGarage({
          schemaVersion: 1,
          kind: 'avt_garage',
          activeBikeKey: exactKey(legacy),
          bikes: [legacy],
        });
        return;
      }
      const selection = activeBikeFrom(garage);
      if (exactKey(legacy) !== exactKey(selection)) writeSelection(selection);
      return;
    }
    if (legacy) {
      writeGarage({
        schemaVersion: 1,
        kind: 'avt_garage',
        activeBikeKey: exactKey(legacy),
        bikes: [legacy],
      });
      return;
    }
    if (rawGarage !== null) {
      try {
        window.localStorage.removeItem(garageStorageKey);
      } catch {
        return;
      }
    }
  };

  const applyRequestedBikeContext = () => {
    let url;
    try {
      url = new URL(window.location?.href ?? '');
    } catch {
      return;
    }

    const requestedBikeKey = String(url.searchParams.get('bike_key') ?? '').trim();
    if (!requestedBikeKey) return;

    const garage = readInternal();
    const requestedBike = garage.bikes.find((bike) => exactKey(bike) === requestedBikeKey);
    if (!requestedBike) return;

    activateBike(requestedBikeKey);

    for (const link of document.querySelectorAll?.('[data-avt-bike-context]') ?? []) {
      try {
        const destination = new URL(link.href, url.origin);
        if (destination.origin !== url.origin) continue;
        destination.searchParams.set('bike_key', requestedBikeKey);
        link.href = destination.href;
      } catch {
        continue;
      }
    }
  };

  initialize();
  applyRequestedBikeContext();

  Object.defineProperty(window, 'AVTGarage', {
    configurable: false,
    enumerable: true,
    writable: false,
    value: Object.freeze({
      activateBike,
      activeBike,
      bikeIsValid,
      clearActiveBike,
      exactKey,
      garageStorageKey,
      maximumBikes,
      normalizeBike,
      read,
      removeBike,
      saveBike,
      selectionStorageKey,
    }),
  });
})();
